import { useState, useEffect, useCallback } from 'react';
import Toast from 'react-native-root-toast';
import { trackEngagementAsync } from '../../util/engagementTracker';
import { showSuccess } from '../../util/error/hookErrors';

import Util from '../../util/util';
import { t } from '../../i18n';

/**
 * useReportData - Custom hook for fetching and managing report data
 *
 * Consolidates report fetching logic shared between ReportDetails.js and AdminReportDetails.js
 *
 * @param {string} uuid - The report UUID to fetch
 * @param {Object} options - Configuration options
 * @param {string} options.defaultOrganization - Fallback organization
 * @param {boolean} options.autoFetch - Whether to fetch on mount (default: true)
 *
 * @returns {Object} Report data and utilities
 */
const useReportData = (uuid, options = {}) => {
  const { defaultOrganization = '', autoFetch = true } = options;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Report fields
  const [report, setReport] = useState(null);
  const [organization, setOrganization] = useState(defaultOrganization);
  const [content, setContent] = useState('');
  const [timestamp, setTimestamp] = useState(null);
  const [reporter, setReporter] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const [isClosed, setIsClosed] = useState(false);

  // Images
  const [images, setImages] = useState([]);
  const [imageURLs, setImageURLs] = useState([]);

  // Comments
  const [comments, setComments] = useState([]);

  // Tags
  const [kpis, setKpis] = useState([]);
  const [locations, setLocations] = useState([]);
  const [hros, setHros] = useState([]);

  // AI fields (admin only)
  const [aiOverridden, setAiOverridden] = useState(false);
  const [aiKpis, setAiKpis] = useState([]);
  const [aiHros, setAiHros] = useState([]);

  /**
   * Fetch the report from API
   */
  const fetchReport = useCallback(async () => {
    if (!uuid) return null;

    setLoading(true);
    setError(null);

    try {
      const data = await Util.get('Resty', '/items/getFromUuid/' + uuid);
      const reportData = data.items;

      setReport(reportData);
      setOrganization(reportData.organization || defaultOrganization);
      setContent(reportData.content || '');
      setTimestamp(reportData.timestamp);
      setReporter(reportData.reporter || '');
      setIsUrgent(reportData.isUrgent === true);
      setIsClosed(reportData.isClosed === true);

      // Images
      if (Array.isArray(reportData.images)) {
        setImages(reportData.images);
      }

      // Comments
      if (Array.isArray(reportData.comments)) {
        setComments(reportData.comments);
      }

      // Tags
      if (Array.isArray(reportData.KPIs)) {
        setKpis(reportData.KPIs.sort());
      }
      if (Array.isArray(reportData.locations)) {
        setLocations(reportData.locations);
      }
      if (Array.isArray(reportData.HROs)) {
        setHros(reportData.HROs);
      }

      // AI fields
      setAiOverridden(reportData.AIOverride === true);
      if (reportData.KPI_AI) {
        setAiKpis([reportData.KPI_AI]);
      }
      if (reportData.HRO_AI) {
        setAiHros([reportData.HRO_AI]);
      }

      // Track report view for surprise badges (shows toast if badge earned)
      trackEngagementAsync('report_view');

      setLoading(false);
      return reportData;
    } catch (err) {
      Util.printAPIError(err);
      setError(err);
      setLoading(false);
      return null;
    }
  }, [uuid, defaultOrganization]);

  /**
   * Fetch signed URLs for images
   */
  const fetchImageURLs = useCallback(async (imageIds) => {
    if (!imageIds || imageIds.length === 0) {
      setImageURLs([]);
      return [];
    }

    // Pass the report uuid so the server can verify access to this report's images
    const promises = imageIds.map((id) => Util.getImageURL(id, false, uuid));
    const urls = await Promise.all(promises);
    setImageURLs(urls);
    return urls;
  }, [uuid]);

  /**
   * Add a comment to the report
   */
  const addComment = useCallback(
    async (commentText, isAdminComment = true) => {
      if (!commentText.trim() || !timestamp) return null;

      const requestData = {
        queryParams: {
          uuid: uuid,
          timestamp: timestamp,
          comment: JSON.stringify(commentText),
          IsNew: true,
          reportId: Util.formatUuid(uuid),
          setFirstCommentDate: !comments || comments.length === 0,
          commentDate: Date.now(),
        },
      };

      try {
        const data = await Util.post('Resty', '/items/addComment', requestData);

        Toast.show(t('reports:ticket.commentAdded'), {
          backgroundColor: '#259e23',
          position: Toast.positions.BOTTOM,
          duration: 2500,
          textColor: '#fff',
        });

        // Track comment engagement for surprise badges (shows toast if badge earned)
        trackEngagementAsync('comment_reply');

        await Util.setReportCache(uuid, 'reportChanged', true);
        await Util.setReportCache(uuid, 'reportCommented', true);

        // Update local state
        const newComment = isAdminComment
          ? commentText
          : { comment: commentText, commenterIsAdmin: false, commentDate: Date.now() };

        setComments((prev) => [...prev, newComment]);

        return data.items;
      } catch (err) {
        Util.printAPIError(err);
        return null;
      }
    },
    [uuid, timestamp, comments],
  );

  /**
   * Edit a comment (within 15 minute window)
   */
  const editComment = useCallback(
    async (commentId, newText) => {
      if (!commentId || !newText?.trim()) return null;

      try {
        const data = await Util.patch('Resty', `/items/comments/${commentId}`, {
          queryParams: { uuid, timestamp },
          body: { comment: newText.trim() },
        });

        showSuccess(t('reports:ticket.commentUpdated'));

        // Refresh to get updated comments
        const updatedReport = await fetchReport();
        if (updatedReport && Array.isArray(updatedReport.comments)) {
          setComments(updatedReport.comments);
        }

        return data.comment;
      } catch (err) {
        Util.printAPIError(err);
        throw new Error(err.error || err.message || t('reports:ticket.commentEditFailed'));
      }
    },
    [uuid, timestamp, fetchReport]
  );

  /**
   * Delete a comment (within 15 minute window)
   */
  const deleteComment = useCallback(
    async (commentId) => {
      if (!commentId) return false;

      try {
        await Util.del('Resty', `/items/comments/${commentId}`, {
          queryParams: { uuid, timestamp },
        });

        showSuccess(t('reports:ticket.commentDeleted'));

        // Refresh to get updated comments
        const updatedReport = await fetchReport();
        if (updatedReport && Array.isArray(updatedReport.comments)) {
          setComments(updatedReport.comments);
        }

        return true;
      } catch (err) {
        Util.printAPIError(err);
        throw new Error(err.error || err.message || t('reports:ticket.commentDeleteFailed'));
      }
    },
    [uuid, timestamp, fetchReport]
  );

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch && uuid) {
      fetchReport();
    }
  }, [autoFetch, uuid, fetchReport]);

  // Fetch image URLs when images change
  useEffect(() => {
    if (images && images.length > 0) {
      fetchImageURLs(images);
    }
  }, [images, fetchImageURLs]);

  return {
    // Loading state
    loading,
    error,

    // Raw report
    report,

    // Basic fields
    organization,
    content,
    timestamp,
    reporter,
    isUrgent,
    isClosed,

    // Images
    images,
    imageURLs,

    // Comments
    comments,
    setComments,

    // Tags
    kpis,
    setKpis,
    locations,
    setLocations,
    hros,
    setHros,

    // AI fields
    aiOverridden,
    setAiOverridden,
    aiKpis,
    aiHros,

    // Actions
    refetch: fetchReport,
    addComment,
    editComment,
    deleteComment,
    fetchImageURLs,

    // Setters for admin edits
    setIsClosed,
    setIsUrgent,
    setOrganization,
  };
};

export default useReportData;
